import { audiosDns } from '@/environment';
import { createTtsAudio } from '@/services/api';
import { useAuth } from '@/services/auth';
import { buildScript, buildTalkTtsInput } from '@/services/d-id/talks';
import { AudioError } from '@/state/presenters';
import { formatS3Url } from '@/utils/s3';
import { IVoice, Providers, VoiceConfig } from '@d-id/types';
import { useCallback, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { activePlayerIdAtom, PlayerMode, playerModeAtom } from './useAudioPlayer';

interface Props {
    voice: IVoice | null;
    text: string;
    voiceConfig: VoiceConfig;
    playerId?: string;
    ttsError?: AudioError | null;
}

export function useAflorithmic({ voice, text, voiceConfig, playerId = '', ttsError }: Props) {
    const { accessToken } = useAuth();

    const [playerMode, setPlayerMode] = useRecoilState(playerModeAtom);

    const [pause, setPause] = useState<VoidFunction>(() => {});
    const setActivePlayerId = useSetRecoilState(activePlayerIdAtom);

    const play = useCallback(
        async (_accessToken: string, _voiceid: string, text: string) => {
            if (playerMode !== PlayerMode.Ready && playerMode !== PlayerMode.NotLoaded) {
                return;
            }
            const script = buildScript(text, voice, { rate: voiceConfig.rate }, false);
            if (!script) return;
            const ttsScript = buildTalkTtsInput(script);
            setPlayerMode(PlayerMode.Loading);
            setActivePlayerId(playerId);
            const { url } = await createTtsAudio({ script: ttsScript });
            const audio = document.createElement('audio');
            audio.src = formatS3Url(audiosDns, url);
            audio.play();
            audio.onplaying = () => setPlayerMode(PlayerMode.Playing);
            audio.onended = () => {
                audio.remove();
                setPlayerMode(PlayerMode.NotLoaded);
                setActivePlayerId('');
            };
            return audio;
        },
        [voice, text, voiceConfig]
    );

    return {
        playerMode,
        error: ttsError,
        pause,
        play: async () => {
            if (voice && text && voice.provider === Providers.Afflorithmics) {
                const player = await play(accessToken, voice.id, text);
                setPause(() => () => {
                    setPlayerMode(PlayerMode.NotLoaded);
                    setActivePlayerId('');
                    player?.pause();
                    player?.remove();
                });
            }
        },
    };
}
